// @flow
import React from 'react';
import gql from 'graphql-tag';
import classNames from 'classnames';

import './GeoDistance.less';

export const fragments = {
    listing: gql`
        fragment GeoDistanceListing on Listing {
            geoDistance {
                text
                relationType
            }
        }
    `
};

export type GeoDistanceSchema = {
    text: string,
    relationType: string
};

// GraphQL can return a null geoDistance field
export type Shape = {
    geoDistance: GeoDistanceSchema | null
};

// But geoDistance is considered required for this component!
export type Props = {
    brand: string,
    geoDistance: GeoDistanceSchema,
    isHighlighted: boolean
};

const defaultProps = {
    isHighlighted: false
};

const getBrandFill = (brand) =>
    brand === 'vrbo' ? 'fill-vrbo-secondary' : 'fill-homeaway-secondary';

const getBrandStroke = (brand) =>
    brand === 'vrbo' ? 'stroke-vrbo-secondary' : 'stroke-homeaway-secondary';

export default function GeoDistance({
    brand,
    geoDistance: {text},
    isHighlighted
}: Props) {
    const geoDistanceClass = classNames('GeoDistance', {
        'GeoDistance--isHighlighted': isHighlighted
    });

    const normalizedBrand = brand.toLowerCase();

    // TODO: get baseline to provide granular fill-secondary and stroke-secondary
    // classes and use those instead!
    const brandFill = getBrandFill(normalizedBrand);
    const brandStroke = getBrandStroke(normalizedBrand);

    /**
     * Visual explanation:
     * - default state is outlined pin shape stroked with current text color,
     *   with solid dot in the middle.
     * - highlighted state is fully opaque pin shape, filled w/ brand secondary
     *   color (highlighted map pin color)
     */
    const iconPinPathClass = classNames(
        'GeoDistance__iconPinPath',
        !isHighlighted ? 'fill-transparent' : brandFill,
        !isHighlighted ? 'stroke-currentColor' : brandStroke
    );

    const iconPinHoleClass = classNames(
        'GeoDistance__iconPinHole',
        !isHighlighted ? 'fill-transparent' : brandFill,
        !isHighlighted ? 'stroke-currentColor' : brandStroke
    );

    return (
        <div className={geoDistanceClass}>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="GeoDistance__icon"
                width="16"
                height="16"
                viewBox="0 0 16 16"
            >
                <g
                    fill="none"
                    fillRule="evenodd"
                    stroke="#5E6D77"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                >
                    <path
                        className={iconPinPathClass}
                        d="M3.95 9.113a5.11 5.11 0 0 1 .546-6.579l.038-.038a5.11 5.11 0 0 1 7.226 0l.037.038a5.11 5.11 0 0 1 .548 6.58L8.147 15 3.95 9.113z"
                    />
                    <path
                        className={iconPinHoleClass}
                        d="M9.84 6.146a1.692 1.692 0 1 1-3.387 0 1.694 1.694 0 0 1 3.387 0z"
                    />
                </g>
            </svg>
            <span className="GeoDistance__text">{text}</span>
        </div>
    );
}

GeoDistance.defaultProps = defaultProps;
